import { useAppDispatch } from "@common-reducers/hooks/store.hook";
import { mediaManager } from "@common-reducers/MediaManagerReducer";
import { handleUpload } from "@common-services/upload";
import { useImageProxy } from "@common/context/ImageProxyContext";
import { useEffect, useState } from "react";
// slate runtime primitives from the live-binding holder (see slate-runtime.ts).
import { Editor, Transforms, Range, useFocused, useSelected } from "@/extension/utils/slate-runtime";
import { ImageElement, LinkElement, ParagraphElement } from "./customTypes";

/**
 *
 * @param editor
 * @returns
 */

export const withImages = (editor: Editor, setIsUploading: any) => {
  const { insertData, isVoid } = editor;

  editor.isVoid = (element: any) => {
    return element.type === "image" ? true : isVoid(element);
  };

  editor.insertData = (data: any) => {

    const { files } = data;

    if (files && files.length > 0) {
      // Save the current selection before async operations (deep copy to preserve state)
      const savedSelection = editor.selection ? JSON.parse(JSON.stringify(editor.selection)) : null;
      
      for (const file of files) {
        const reader = new FileReader();
        const mimeType: string = file.type;
        const filename: string = file.name;

        reader.addEventListener("load", () => {
          const base64file: any = reader.result;
          insertImage(editor, base64file, mimeType, setIsUploading, filename, savedSelection);
          setIsUploading(true);
        });
        reader.readAsDataURL(file);
      }
    } else {
      insertData(data);
    }
  };

  return editor;
};

//Inset the image into the editor
const insertImage = async (
  editor: Editor,
  base64file: string,
  mimeType: string,
  setIsUploading: any,
  filename: string,
  savedSelection?: any
) => {
  // get the uploaded image's url and then insert the image into the editor nodes

  try {
    const response: any = await handleUpload(base64file, mimeType);
    if (response.uploadResponse.url) {
      // Use saved selection if provided, otherwise use current selection
      const selectionToUse = savedSelection || editor.selection;
      
      // Check if there's a selection and if it's expanded (not just a cursor)
      if (selectionToUse && Range.isRange(selectionToUse)) {
        // Check if the selection is expanded (not collapsed)
        const isExpanded = !Range.isCollapsed(selectionToUse);
        if (isExpanded) {
          // Restore the selection first, then delete the selected content
          Transforms.select(editor, selectionToUse);
          Transforms.delete(editor);
        } else {
          // Just position cursor at the saved location
          Transforms.select(editor, selectionToUse);
        }
      }

      const image: ImageElement = {
        type: "image",
        url: response.image_url,
        children: [{ text: "" }],
      };
      const Link: LinkElement = {
        type: "link",
        url: response.image_url,
        children: [{ text: filename }],
      };
      const paragraph: ParagraphElement = {
        type: "paragraph",
        children: [{ text: "", bold: false }],
      };

      // if mimeType is not image insert the link element
      if (!mimeType.includes("image")) {
        Transforms.insertNodes(editor, Link);
        setIsUploading(false);
      } else {
        Transforms.insertNodes(editor, image);
        Transforms.insertNodes(editor, paragraph);
        setIsUploading(false);
      }
    }
  } catch (err) {
    //dispatch(extraDataActions.setEditorUploading(false))
  }
};

export const __ImageElement = ({ attributes, children, element }: any) => {
  const dispatch = useAppDispatch()
  const { processUrl } = useImageProxy();
  const [originalUrl, setOriginalUrl] = useState<string>("");
  const [imgSrc, setImgSrc] = useState<string>("");
  const [retryStep, setRetryStep] = useState<number>(0);

  const getUrlFromElement = (node: any): string => {
    const directCandidates = [
      node?.url,
      node?.src,
      node?.imageUrl,
      node?.imageURL,
      node?.asset?.url,
      node?.image?.url,
      node?.file?.url,
    ];

    for (const candidate of directCandidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
      }
    }

    // Legacy/edge data: URL may appear inside children nodes.
    if (Array.isArray(node?.children)) {
      for (const child of node.children) {
        if (typeof child?.url === "string" && child.url.trim()) {
          return child.url.trim();
        }
        if (typeof child?.text === "string" && /^https?:\/\//i.test(child.text.trim())) {
          return child.text.trim();
        }
      }
    }

    return "";
  };

  const stripQueryParams = (url: string): string => {
    try {
      const parsed = new URL(url);
      parsed.search = "";
      return parsed.toString();
    } catch {
      return url;
    }
  };

  useEffect(() => {
    let isActive = true;
    const resolvedOriginalUrl = getUrlFromElement(element);

    setOriginalUrl(resolvedOriginalUrl);
    setRetryStep(0);

    if (!resolvedOriginalUrl) {
      setImgSrc("");
      return () => {
        isActive = false;
      };
    }

    processUrl(resolvedOriginalUrl)
      .then((resolvedUrl) => {
        if (!isActive) return;
        setImgSrc(resolvedUrl || resolvedOriginalUrl);
      })
      .catch(() => {
        if (!isActive) return;
        setImgSrc(resolvedOriginalUrl);
      });

    return () => {
      isActive = false;
    };
  }, [element, processUrl]);

  function previewImage() {

    // dispatch(
    //   previewPhoto({ isActive: !imagePreview.isActive, imageURL: element.url })
    // );

    const currentFilesAndIndex: Record<string, unknown> = {}
    currentFilesAndIndex["index"] = 0;
    currentFilesAndIndex["shown"] = true;
    currentFilesAndIndex["filesTotal"] = [{ url: imgSrc || originalUrl, type: "image" }]

    currentFilesAndIndex["isViewOnly"] = true;

    dispatch(
      mediaManager.setSelectedCellFilesAndCurrentindex(currentFilesAndIndex)
    );


  }
  const selected = useSelected();
  const focused = useFocused();
  return (
    <div {...attributes}>
      <div contentEditable={false}>
        <img
          onClick={previewImage}
          src={imgSrc}
          onError={() => {
            if (!originalUrl) return;

            // 1) If proxied/blob URL failed, try the original URL.
            if (retryStep === 0 && imgSrc !== originalUrl) {
              setRetryStep(1);
              setImgSrc(originalUrl);
              return;
            }

            // 2) If URL looks like signed URL and expired, retry without query params.
            if (retryStep <= 1) {
              const noQueryUrl = stripQueryParams(originalUrl);
              if (noQueryUrl && noQueryUrl !== imgSrc) {
                setRetryStep(2);
                setImgSrc(noQueryUrl);
                return;
              }
            }

            // 3) Retry with encoded URL (spaces/special chars in file names).
            if (retryStep <= 2) {
              const encodedUrl = encodeURI(originalUrl);
              if (encodedUrl && encodedUrl !== imgSrc) {
                setRetryStep(3);
                setImgSrc(encodedUrl);
              }
            }
          }}
          alt=""
          style={{
            display: "block",
            width: '100%',
            height: '100%',
            maxHeight: "20em",
            objectFit: 'contain',
            boxShadow: selected && focused ? "0 0 0 3px #B4D5FF" : "none",
            cursor: "pointer",
          }}
        />
      </div>
      {children}
    </div>
  );
};
